import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

const solutionDetails = {
  'practice-management': {
    title: 'Practice Management Solution',
    hero: {
      title: 'Streamline Your Dental Practice Operations',
      subtitle: 'Comprehensive management solution designed for modern dental practices',
      image: '/assets/practice-management-hero.webp'
    },
    stats: [
      { value: '98%', label: 'Customer Satisfaction' },
      { value: '45%', label: 'Time Saved' },
      { value: '2x', label: 'Revenue Growth' },
      { value: '30%', label: 'Cost Reduction' }
    ],
    features: [
      {
        title: 'Smart Scheduling',
        description: 'AI-powered scheduling system that optimizes appointment slots and reduces no-shows',
        icon: (
          <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        )
      },
      {
        title: 'Financial Analytics',
        description: 'Real-time financial reporting and analytics to track practice performance',
        icon: (
          <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        )
      },
      {
        title: 'Patient Communication',
        description: 'Integrated patient communication tools for better engagement and satisfaction',
        icon: (
          <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        )
      },
      {
        title: 'Inventory Management',
        description: 'Automated inventory tracking and reordering system for dental supplies',
        icon: (
          <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
        )
      }
    ],
    benefits: [
      {
        title: 'Streamlined Operations',
        description: 'Automate routine tasks and streamline your practice workflow',
        image: '/assets/streamlined-operations.webp'
      },
      {
        title: 'Enhanced Patient Care',
        description: 'Improve patient experience with better communication and care coordination',
        image: '/assets/patient-care.webp'
      },
      {
        title: 'Increased Revenue',
        description: 'Optimize billing and collections to maximize practice revenue',
        image: '/assets/revenue-growth.webp'
      },
      {
        title: 'Better Team Management',
        description: 'Efficient staff scheduling and performance tracking tools',
        image: '/assets/team-management.webp'
      }
    ],
    testimonials: [
      {
        quote: "EzzyDental has transformed how we manage our practice. The automated scheduling and patient communication features have saved us countless hours.",
        author: "Dr. Sarah Johnson",
        position: "Owner, Bright Smile Dental"
      },
      {
        quote: "The financial analytics and reporting tools have given us unprecedented insight into our practice performance. We've seen a 30% increase in revenue since implementing EzzyDental.",
        author: "Dr. Michael Chen",
        position: "Practice Owner, Modern Dental Group"
      }
    ],
    extendedFeatures: [
      {
        title: 'Appointment Management',
        description: 'Smart scheduling with automated reminders and follow-ups',
        icon: (
          <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        )
      },
      {
        title: 'Billing & Insurance',
        description: 'Automated insurance verification and claim processing',
        icon: (
          <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        )
      },
      {
        title: 'Patient Records',
        description: 'Digital patient records with comprehensive documentation',
        icon: (
          <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        )
      },
      {
        title: 'Reporting & Analytics',
        description: 'Comprehensive reporting and business intelligence',
        icon: (
          <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        )
      },
      {
        title: 'Staff Management',
        description: 'Comprehensive staff scheduling and performance tracking',
        icon: (
          <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        )
      },
      {
        title: 'Inventory Control',
        description: 'Automated inventory tracking and supply management',
        icon: (
          <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
        )
      }
    ],
    features_extended: [
      {
        title: 'Automated Billing',
        description: 'Streamlined billing process with automated payment reminders and processing'
      },
      {
        title: 'Insurance Management',
        description: 'Automated insurance verification and claim submission system'
      },
      {
        title: 'Patient Portal',
        description: 'Secure online portal for patients to access their records and schedule appointments'
      },
      {
        title: 'Staff Scheduling',
        description: 'Advanced staff scheduling with shift management and time tracking'
      },
      {
        title: 'Inventory Tracking',
        description: 'Real-time inventory management with automated reordering'
      },
      {
        title: 'Performance Analytics',
        description: 'Comprehensive practice performance metrics and reporting'
      }
    ]
  },
  'patient-engagement': {
    title: 'Patient Engagement Solution',
    hero: {
      title: 'Transform Patient Experience',
      subtitle: 'Build lasting relationships with your patients through modern engagement tools',
      image: '/assets/patient-engagement-hero.webp'
    },
    stats: [
      { value: '98%', label: 'Patient Satisfaction' },
      { value: '75%', label: 'Reduced No-Shows' },
      { value: '4.8/5', label: 'Patient Rating' },
      { value: '40%', label: 'Time Saved' }
    ],
    features: [
      {
        title: 'Smart Reminders',
        description: 'AI-powered appointment reminders with personalized messaging and automated follow-ups',
        icon: (
          <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        )
      },
      {
        title: 'Online Booking',
        description: '24/7 self-service appointment booking with real-time availability and instant confirmation',
        icon: (
          <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        )
      },
      {
        title: 'Patient Portal',
        description: 'Secure portal for accessing medical records, treatment plans, and communication with your practice',
        icon: (
          <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      },
      {
        title: 'Digital Forms',
        description: 'Paperless patient forms with pre-filled information and electronic signatures',
        icon: (
          <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        )
      }
    ],
    benefits: [
      {
        title: 'Improved Communication',
        description: 'Keep patients informed and engaged with automated, personalized communications',
        image: '/assets/improved-communication.webp'
      },
      {
        title: 'Reduced No-Shows',
        description: 'Smart reminder system significantly reduces appointment no-shows and cancellations',
        image: '/assets/reduced-no-shows.webp'
      },
      {
        title: 'Enhanced Satisfaction',
        description: 'Provide convenient, modern tools that patients expect from their healthcare providers',
        image: '/assets/enhanced-satisfaction.webp'
      },
      {
        title: 'Streamlined Workflow',
        description: 'Automate patient intake and reduce administrative tasks for your staff',
        image: '/assets/streamlined-workflow.webp'
      }
    ],
    testimonials: [
      {
        quote: "Since implementing the patient engagement system, our no-show rate has dropped by 75% and patient satisfaction has soared. The automated reminders and online booking have made life easier for both our staff and patients.",
        author: "Dr. Emily Chen",
        position: "Director, Modern Smile Dental"
      },
      {
        quote: "The patient portal and digital forms have transformed how we interact with our patients. They love the convenience, and we've seen a significant reduction in administrative work.",
        author: "Dr. Michael Roberts",
        position: "Owner, Bright Smile Dental"
      }
    ],
    features_extended: [
      {
        title: 'Two-Way Messaging',
        description: 'Enable secure, HIPAA-compliant messaging between staff and patients',
        icon: (
          <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        )
      },
      {
        title: 'Review Management',
        description: 'Automatically request and manage patient reviews to build your online reputation',
        icon: (
          <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
          </svg>
        )
      },
      {
        title: 'Treatment Plan Communication',
        description: 'Share detailed treatment plans and educational content with patients',
        icon: (
          <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        )
      },
      {
        title: 'Digital Forms',
        description: 'Send and receive patient forms electronically before appointments',
        icon: (
          <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        )
      },
      {
        title: 'Multi-Channel Communication',
        description: 'Reach patients via their preferred communication method (SMS, email, or app)',
        icon: (
          <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        )
      },
      {
        title: 'Automated Recalls',
        description: 'Smart recall system to bring patients back for regular care',
        icon: (
          <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      }
    ]
  },
  'clinical-tools': {
    title: 'AI-Powered Clinical Tools for Modern Dentistry',
    hero: {
      title: 'AI-Powered Clinical Tools for Modern Dentistry',
      subtitle: 'Transform your clinical workflow with advanced digital tools and AI-powered X-ray analysis',
      image: '/assets/clinical-tools-hero.webp'
    },
    stats: [
      { value: '95%', label: 'AI Detection Accuracy' },
      { value: '40%', label: 'Faster Diagnoses' },
      { value: '99.9%', label: 'Clinical Accuracy' },
      { value: '50%', label: 'Time Saved' }
    ],
    features: [
      {
        title: 'AI X-Ray Analysis',
        description: 'Advanced AI-powered system that analyzes dental X-rays to detect cavities and anatomical features with high accuracy',
        icon: (
          <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
          </svg>
        )
      },
      {
        title: 'Digital Charting',
        description: 'Comprehensive digital charting system with intuitive interface and real-time updates',
        icon: (
          <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        )
      },
      {
        title: 'Treatment Planning',
        description: 'Visual treatment planning tools with 3D modeling and patient education resources',
        icon: (
          <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
          </svg>
        )
      },
      {
        title: 'Clinical Notes',
        description: 'Voice-to-text clinical notes with customizable templates and quick-entry features',
        icon: (
          <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
          </svg>
        )
      }
    ],
    benefits: [
      {
        title: 'AI-Powered Diagnostics',
        description: 'Advanced AI system analyzes X-rays to detect cavities and anatomical features with 95% accuracy',
        image: '/assets/ai-diagnostics.webp'
      },
      {
        title: 'Enhanced Clinical Accuracy',
        description: 'AI assistance helps ensure precise diagnoses and treatment planning',
        image: '/assets/clinical-accuracy.webp'
      },
      {
        title: 'Streamlined Workflows',
        description: 'Automated X-ray analysis and integrated systems save time and reduce errors',
        image: '/assets/streamlined-workflow.webp'
      },
      {
        title: 'Better Patient Education',
        description: 'Visual AI analysis helps patients understand their dental health and treatment options',
        image: '/assets/patient-education.webp'
      }
    ],
    testimonials: [
      {
        quote: "The AI-powered X-ray analysis has revolutionized our diagnostic process. It's like having an expert second opinion on every X-ray, helping us catch issues we might have missed and improving our treatment planning.",
        author: "Dr. Michael Roberts",
        position: "Clinical Director, Advanced Dental Care"
      },
      {
        quote: "The combination of AI analysis and 3D treatment planning has significantly improved our diagnostic accuracy and case acceptance rate. Patients can now see exactly what we're looking at in their X-rays.",
        author: "Dr. Sarah Chen",
        position: "Owner, Modern Smile Dental"
      }
    ],
    features_extended: [
      {
        title: 'AI X-Ray Analysis',
        description: 'Advanced AI system that analyzes dental X-rays to detect cavities and anatomical features',
        icon: (
          <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
          </svg>
        )
      },
      {
        title: 'Periodontal Charting',
        description: 'Comprehensive periodontal examination tools with automated PSR calculations',
        icon: (
          <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        )
      },
      {
        title: 'Treatment Sequences',
        description: 'Create and manage complex treatment plans with custom sequences and phasing',
        icon: (
          <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        )
      },
      {
        title: 'Clinical Notes',
        description: 'Voice-to-text clinical notes with customizable templates and quick-entry features',
        icon: (
          <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
          </svg>
        )
      },
      {
        title: 'Image Enhancement',
        description: 'Advanced image processing tools for better diagnostics and patient communication',
        icon: (
          <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        )
      },
      {
        title: 'Clinical Analytics',
        description: 'Track clinical outcomes and identify trends with advanced analytics',
        icon: (
          <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        )
      }
    ]
  },
  'pricing': {
    title: 'Pricing Solutions',
    hero: {
      title: 'Transparent Pricing for Every Practice Size',
      subtitle: 'Flexible pricing plans designed to scale with your dental organization',
      image: '/assets/pricing-hero.webp'
    },
    stats: [
      { value: '30%', label: 'Average Cost Savings' },
      { value: '24/7', label: 'Priority Support' },
      { value: '99.9%', label: 'Uptime Guarantee' }
    ],
    pricingTiers: [
      {
        name: 'Single Office',
        price: 499,
        offices: '1 Office',
        totalCost: '$499',
        features: [
          'Full EzzyDental Platform',
          'Standard Support',
          'Basic Reporting',
          'Cloud Storage',
          'Regular Updates'
        ]
      },
      {
        name: 'Growing Practice',
        price: 599,
        offices: '2-5 Offices',
        totalCost: '$1,198 - $2,995',
        features: [
          'Premium Support',
          'Multi-location Dashboard',
          'Advanced Reporting',
          'Team Training',
          'Priority Updates'
        ]
      },
      {
        name: 'Established DSO',
        price: 650,
        offices: '6-10 Offices',
        totalCost: '$3,900 - $6,500',
        features: [
          'Custom Reporting',
          'Priority Support',
          'Dedicated Account Manager',
          'Custom Integrations',
          'Advanced Security'
        ]
      },
      {
        name: 'Enterprise',
        price: '650+',
        offices: '10+ Offices',
        totalCost: '$6,500+',
        features: [
          'Tailored Features',
          'API Integrations',
          'Custom Development',
          'Enterprise Support',
          'Unlimited Storage'
        ]
      }
    ],
    enterpriseFeatures: [
      {
        title: 'Dedicated Account Manager',
        description: 'Your personal point of contact for all needs',
        icon: (
          <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      },
      {
        title: '24/7 Priority Support',
        description: 'Round-the-clock assistance for critical issues',
        icon: (
          <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        )
      },
      {
        title: 'Custom API Integrations',
        description: 'Connect with your existing systems seamlessly',
        icon: (
          <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        )
      }
    ],
    onboardingOptions: [
      {
        title: 'Enterprise Onboarding',
        type: 'Included',
        description: 'Free onboarding for 10+ locations',
        features: [
          'Dedicated Account Manager',
          'One-on-One Training Calls',
          'Multi-Location Setup',
          'Data Migration',
          'Custom API Integrations',
          '24/7 Priority Support',
          'Team Workshops & Webinars',
          'Ongoing Performance Optimization'
        ]
      },
      {
        title: 'Premium Onboarding',
        type: 'One-Time Fee',
        description: 'For practices with 5+ locations',
        price: '$2,500',
        features: [
          'Professional Setup',
          'Staff Training',
          'Data Migration',
          'Basic Integrations',
          'Initial Performance Setup'
        ]
      },
      {
        title: 'Enterprise Setup',
        type: 'One-Time Fee',
        description: 'For practices with 10+ locations',
        price: '$5,000',
        features: [
          'Complete Enterprise Setup',
          'Advanced Integrations',
          'Custom Workflow Design',
          'Extended Training Program',
          'Performance Optimization'
        ]
      }
    ],
    testimonials: [
      {
        quote: "The enterprise package has been a game-changer for our multi-location practice. The dedicated support and custom integrations have streamlined our operations significantly.",
        author: "Dr. James Wilson",
        position: "CEO, Pacific Dental Group (15 locations)"
      }
    ]
  }
};

function SolutionDetail() {
  const { id } = useParams();
  const solution = solutionDetails[id];
  const [selectedOnboarding, setSelectedOnboarding] = useState(null);

  if (!solution) {
    return <div>Solution not found</div>;
  }

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#E5F0F3] to-white">
        <div className="container mx-auto px-6 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-[#1E2A4A] mb-6">
                {solution.hero.title}
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                {solution.hero.subtitle}
              </p>
              <button className="bg-orange-500 text-white px-8 py-3 rounded-full font-medium hover:bg-orange-600 transition-colors">
                Schedule Demo
              </button>
            </div>
            <div className="relative">
              <img
                src={solution.hero.image}
                alt={solution.hero.title}
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-orange-500/20 to-transparent rounded-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {solution.stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-4xl font-bold text-orange-500 mb-2">{stat.value}</div>
              <div className="text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Core Features Section */}
      <div className="bg-gray-50 py-16">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-[#1E2A4A] text-center mb-12">
            Core Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {solution.features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-all hover:-translate-y-1">
                <div className="mb-6">{feature.icon}</div>
                <h3 className="text-xl font-bold text-[#1E2A4A] mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="container mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-[#1E2A4A] text-center mb-12">
          Key Benefits
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {solution.benefits.map((benefit, index) => (
            <div key={index} className="relative group">
              <img
                src={benefit.image}
                alt={benefit.title}
                className="w-full h-64 object-cover rounded-xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                  <p>{benefit.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Extended Features Section */}
      {solution.extendedFeatures && (
        <div className="bg-gray-50 py-16">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl font-bold text-[#1E2A4A] text-center mb-12">
              Extended Features
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {solution.extendedFeatures.map((feature, index) => (
                <div key={index} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all">
                  <div className="flex items-center mb-4">
                    <div className="mr-4">{feature.icon}</div>
                    <h3 className="text-lg font-bold text-[#1E2A4A]">{feature.title}</h3>
                  </div>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Pricing Tiers */}
      {solution.pricingTiers && (
        <div className="container mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold text-[#1E2A4A] text-center mb-12">
            Pricing Plans for Every Practice Size
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {solution.pricingTiers.map((tier, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-shadow p-6">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-[#1E2A4A] mb-2">{tier.name}</h3>
                  <div className="text-3xl font-bold text-orange-500 mb-1">
                    ${tier.price}<span className="text-sm text-gray-500">/office/mo</span>
                  </div>
                  <div className="text-gray-600 mb-2">{tier.offices}</div>
                  <div className="text-sm text-gray-500">Total: {tier.totalCost}</div>
                </div>
                <ul className="space-y-3">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-600">
                      <svg className="w-5 h-5 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
                <button className="w-full mt-6 px-6 py-3 bg-orange-500 text-white rounded-full font-medium hover:bg-orange-600 transition-colors">
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Enterprise Features */}
      {solution.enterpriseFeatures && (
        <div className="bg-gray-50 py-16">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl font-bold text-[#1E2A4A] text-center mb-12">
              Enterprise-Grade Features
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {solution.enterpriseFeatures.map((feature, index) => (
                <div key={index} className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-all hover:-translate-y-1">
                  <div className="mb-6">{feature.icon}</div>
                  <h3 className="text-xl font-bold text-[#1E2A4A] mb-4">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Onboarding Options */}
      {solution.onboardingOptions && (
        <div className="container mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold text-[#1E2A4A] text-center mb-12">
            Custom Onboarding & Training
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {solution.onboardingOptions.map((option, index) => (
              <div 
                key={index} 
                className={`bg-white rounded-xl shadow-sm hover:shadow-lg transition-all p-6 ${
                  selectedOnboarding === index ? 'ring-2 ring-orange-500' : ''
                }`}
                onClick={() => setSelectedOnboarding(index)}
              >
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-[#1E2A4A] mb-2">{option.title}</h3>
                  <div className="text-orange-500 font-medium mb-2">{option.type}</div>
                  {option.price && (
                    <div className="text-2xl font-bold text-[#1E2A4A] mb-2">{option.price}</div>
                  )}
                  <p className="text-gray-600">{option.description}</p>
                </div>
                <ul className="space-y-3">
                  {option.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-600">
                      <svg className="w-5 h-5 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
                <button className="w-full mt-6 px-6 py-3 bg-orange-500 text-white rounded-full font-medium hover:bg-orange-600 transition-colors">
                  Select Plan
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Testimonials Section */}
      <div className="bg-[#E5F0F3] py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            {solution.testimonials.map((testimonial, index) => (
              <div key={index} className="text-center">
                <svg className="w-12 h-12 text-orange-500 mx-auto mb-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
                <blockquote className="text-2xl text-gray-700 italic mb-8">
                  "{testimonial.quote}"
                </blockquote>
                <div className="font-semibold text-[#1E2A4A]">{testimonial.author}</div>
                <div className="text-gray-500">{testimonial.position}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="container mx-auto px-6 py-16 text-center">
        <h2 className="text-3xl font-bold text-[#1E2A4A] mb-6">
          Ready to Scale Your Dental Organization?
        </h2>
        <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
          Join leading dental practices that trust EzzyDental for their practice management needs.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button className="bg-orange-500 text-white px-8 py-3 rounded-full font-medium hover:bg-orange-600 transition-colors">
            Schedule Demo
          </button>
          <button className="bg-white text-orange-500 px-8 py-3 rounded-full font-medium border-2 border-orange-500 hover:bg-orange-50 transition-colors">
            Contact Sales
          </button>
        </div>
      </div>
    </div>
  );
}

export default SolutionDetail; 